import type { TraceSearchHit } from '@api/cm-sites';

export interface BatchSuggestion {
    value: string;
    label: string;
}

/**
 * Collapse the batch list into one suggestion per distinct code.
 *
 * A batch is identified by (merchant, origin, code, item), so one code legitimately names
 * several batches — the same supplier lot number arriving from two suppliers, or a site reusing
 * a run number for a different item. The trace field searches BY CODE and the search already
 * returns every batch that matches, so a repeated code adds nothing to choose between. Mantine
 * also rejects duplicate option values outright, which took the whole Trace page down.
 *
 * Where a code does cover more than one item, say so rather than naming whichever batch
 * happened to sort first — the count is true, an arbitrary pick would not be.
 *
 * Insertion order is preserved so the server's ordering (most recent first) survives.
 */
export function buildBatchSuggestions(batches: TraceSearchHit[]): BatchSuggestion[] {
    const byCode = new Map<string, Set<string>>();

    for (const batch of batches) {
        const names = byCode.get(batch.code) ?? new Set<string>();
        if (batch.itemName) names.add(batch.itemName);
        byCode.set(batch.code, names);
    }

    return [...byCode].map(([code, names]) => {
        if (names.size === 1) return { value: code, label: `${code} · ${[...names][0]}` };
        if (names.size > 1) return { value: code, label: `${code} · ${names.size} items` };
        // No item name resolved for any of them — the bare code is all we can honestly show.
        return { value: code, label: code };
    });
}
